import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, catchError, tap, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UserGroup, UserGroupDto, UserGroupMapper } from '../../model/user-group';
import { Permission } from '../../model/beyond-supplier-interface';

@Injectable({
  providedIn: 'root'
})
export class GroupService {

  constructor(private _http: HttpClient) { }

  /**
   * Get user groups for a given company ID
   * 
   * @param companyId The ID of the company to get groups for
   * @returns An observable that emits the list of user groups
   */
  public getUserGroups(companyId: number): Observable<UserGroup[]> {
    return this._http.get<UserGroupDto[]>(`${environment.baseUrl}/groups/${companyId}`).pipe(
      map((groupDtos: UserGroupDto[]) => {
        // Convert DTOs to UserGroup models
        return groupDtos.map(dto => UserGroupMapper.toModel(dto));
      }),
      tap({
        next: (groups: UserGroup[]) => {
          console.log(`[GROUP]: successfully fetched ${groups.length} groups for company ${companyId}`);
        },
        error: (error) => {
          console.error(`[GROUP]: failed to fetch groups for company ${companyId}`, error);
        }
      }),
      catchError((error) => {
        console.error(`[GROUP]: error fetching groups for company ${companyId}`, error);
        return of([]);
      })
    );
  }

  /**
   * Get all permissions for a company ID and supplied role
   * 
   * @param companyId The ID of the company
   * @param role The role to get permissions for ('BORROWER' or 'SUPPLIER')
   * @returns An observable that emits the list of permissions
   */
  public getAllPermissions(companyId: number, role: 'BORROWER' | 'SUPPLIER'): Observable<Permission[]> {
    return this._http.get<string[]>(`${environment.baseUrl}/permissions/${companyId}/${role.toLowerCase()}`).pipe(
      map((permissionStrings: string[]) => {
        // Convert permission strings to Permission objects
        return permissionStrings.map(permission => ({
          id: 0, // ID will be assigned when saved
          companyId: companyId,
          permission: permission
        } as Permission));
      }),
      tap({
        next: (permissions: Permission[]) => {
          console.log(`[GROUP]: successfully fetched ${permissions.length} permissions for company ${companyId} role ${role}`);
        },
        error: (error) => {
          console.error(`[GROUP]: failed to fetch permissions for company ${companyId} role ${role}`, error);
        }
      }),
      catchError((error) => {
        console.error(`[GROUP]: error fetching permissions for company ${companyId} role ${role}`, error);
        return of([]);
      })
    );
  }
}
